import React, { useState, useRef, useEffect } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'

// Libs

// Components
import Icon from '@/assets/icons'
import LanguageList from '@/components/layouts/BasicLayout/components/Header/component/LanguageList'
import { HeaderActionType } from '@/components/layouts/BasicLayout/components/Header/reducer/headerReducer'

// Constants
import { LOCALE_LABEL } from '@/constants/defaultLocale'

// Styles
import styles from './style.module.scss'

// Variables
const cx = classnames.bind(styles)

type LanguageItem = {
  label: string
  value: string
}

interface LanguageSelectorProps {
  className?: string
  iconColor?: string
  textColor?: string
  languageList?: LanguageItem[]
}

function LanguageSelector(props: LanguageSelectorProps) {
  const { className, iconColor, textColor, languageList } = props
  const router = useRouter()
  const [isLanguageListOpen, setIsLanguageListOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 })
  const languageSelectorRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleLanguageListOpen = () => {
    if (!isLanguageListOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      })
    }
    setIsLanguageListOpen(!isLanguageListOpen)
  }

  const handleLanguageListClose = () => {
    setIsLanguageListOpen(false)
  }

  // 創建一個簡化的 dispatch 函數來配合 LanguageList 組件
  const headerDispatch = (action: { type: HeaderActionType }) => {
    if (action.type === HeaderActionType.HANDLE_LANGUAGE_LIST_OPEN) {
      setIsLanguageListOpen(false)
    }
  }

  // 點擊外部關閉語言列表
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        isLanguageListOpen &&
        languageSelectorRef.current &&
        dropdownRef.current &&
        !languageSelectorRef.current.contains(target) &&
        !dropdownRef.current.contains(target)
      ) {
        setIsLanguageListOpen(false)
      }
    }

    if (isLanguageListOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isLanguageListOpen])

  return (
    <div className={cx('language-selector', className)} ref={languageSelectorRef}>
      <div className={cx('language-selector__button')} ref={buttonRef} onClick={handleLanguageListOpen}>
        <div className={cx('language-selector__icon')} style={{ color: iconColor }}>
          <Icon.Earth />
        </div>
        <div className={cx('language-selector__text')} style={{ color: textColor }}>
          {LOCALE_LABEL[router.locale as string]}
        </div>
      </div>
      {isLanguageListOpen && (
        <>
          <div className={cx('language-selector__overlay')} onClick={handleLanguageListClose}></div>
          <div
            ref={dropdownRef}
            className={cx('language-selector__dropdown')}
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
            }}
          >
            <LanguageList headerDispatch={headerDispatch} languageList={languageList} />
          </div>
        </>
      )}
    </div>
  )
}

export default LanguageSelector
